import type { InvoiceStatus } from '@/gql/graphql'
import {
  DRAFT_STATUSES,
  OUTSTANDING_STATUSES,
  PAID_STATUSES,
  PENDING_REVIEW_STATUSES,
  invoiceAmount,
  invoiceRef,
} from './invoice-utils'

/**
 * The CFO "Invoices & Billing" cockpit's derived model — every grouping, label and figure the page
 * shows, as pure functions over the invoice rows. Nothing here invents data: where the schema does
 * not carry a fact (payment date, approval rule, FX rate), the model returns nothing for it and the
 * page renders that part greyed (PendingFeature), never a guess.
 */

// ── Tabs ──────────────────────────────────────────────────────────────

/** Waiting on this customer: a decision to approve/reject, or a query on hold with the supplier. */
export const ACTION_REQUIRED_STATUSES: InvoiceStatus[] = [...PENDING_REVIEW_STATUSES, 'HOLD']

/** Approved and on its way to being paid. */
export const APPROVED_SCHEDULED_STATUSES: InvoiceStatus[] = [
  'APPROVED',
  'VERIFIED',
  'PAYOUT_SCHEDULED',
  'PAYMENT_INITIATED',
  'PARTIALLY_PAID',
  'OVERDUE',
]

/** Unpaid liability: everything still owed, INCLUDING what is on hold. */
export const OPEN_LIABILITY_STATUSES: InvoiceStatus[] = [...new Set<InvoiceStatus>([...OUTSTANDING_STATUSES, 'HOLD'])]

export type CockpitTab = 'action' | 'drafts' | 'approved' | 'awaiting' | 'paid' | 'all' | 'credit-notes'

/**
 * Which side of the ledger the cockpit is for. `payables` — a payer's view (a client, an agency): what
 * to approve and what is owed. `receivables` — a raiser's view (a vendor): what it raised and what it
 * is still owed; it decides nothing, the customer does.
 */
export type CockpitSide = 'payables' | 'receivables'

export interface CockpitTabSpec {
  value: CockpitTab
  label: string
  statuses?: InvoiceStatus[]
}

export const COCKPIT_TABS: CockpitTabSpec[] = [
  { value: 'action', label: 'Action required', statuses: ACTION_REQUIRED_STATUSES },
  { value: 'approved', label: 'Approved / scheduled', statuses: APPROVED_SCHEDULED_STATUSES },
  { value: 'paid', label: 'Paid / settled', statuses: PAID_STATUSES },
  { value: 'all', label: 'All invoices' },
  { value: 'credit-notes', label: 'Credit notes' },
]

const DRAFTS_TAB: CockpitTabSpec = { value: 'drafts', label: 'Drafts', statuses: DRAFT_STATUSES }

/** A vendor's tabs: its drafts, what the customer still owes it, what is paid. */
export const RECEIVABLE_TABS: CockpitTabSpec[] = [
  DRAFTS_TAB,
  { value: 'awaiting', label: 'Awaiting payment', statuses: OPEN_LIABILITY_STATUSES },
  { value: 'paid', label: 'Paid', statuses: PAID_STATUSES },
  { value: 'all', label: 'All invoices' },
  { value: 'credit-notes', label: 'Credit notes' },
]

/**
 * The tabs for a persona. One that raises invoices (an agency) also needs its own saved-but-not-
 * raised drafts, right after the action queue; a pure payer (a client) has none.
 */
export function cockpitTabsFor(opts: { drafts: boolean; side?: CockpitSide }): CockpitTabSpec[] {
  if (opts.side === 'receivables') return RECEIVABLE_TABS
  return opts.drafts ? [COCKPIT_TABS[0], DRAFTS_TAB, ...COCKPIT_TABS.slice(1)] : COCKPIT_TABS
}

/** The tab a side opens on (and omits from the URL): a payer's decision queue, a raiser's whole book. */
export function defaultTabFor(side: CockpitSide = 'payables'): CockpitTab {
  return side === 'receivables' ? 'all' : 'action'
}

// ── Dates ─────────────────────────────────────────────────────────────

const DAY = 24 * 60 * 60 * 1000

/** Whole days from `today` to `date` (both yyyy-mm-dd or ISO), compared as calendar days in UTC. */
export function daysUntil(date: string, today: Date): number {
  const d = Date.UTC(+date.slice(0, 4), +date.slice(5, 7) - 1, +date.slice(8, 10))
  const t = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())
  return Math.round((d - t) / DAY)
}

export interface DueAging {
  label: string
  tone: 'default' | 'danger' | 'muted'
}

/** "28 Aug" — day and short month, en-GB (the app's date locale). */
function dayMonth(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

/** "Due tomorrow", "Overdue 3 days", "Paid 28 Aug" — the mockup's due & aging line. */
export function dueAging(
  inv: { status: InvoiceStatus; dueDate?: string | null; paidAt?: string | null },
  today: Date,
): DueAging {
  if (PAID_STATUSES.includes(inv.status)) {
    return { label: inv.paidAt ? `Paid ${dayMonth(inv.paidAt)}` : 'Settled', tone: 'muted' }
  }
  if (inv.status === 'CANCELLED' || inv.status === 'VOID' || inv.status === 'REJECTED') {
    return { label: 'Closed', tone: 'muted' }
  }
  if (!inv.dueDate) return { label: 'Due on receipt', tone: 'default' }
  const days = daysUntil(inv.dueDate, today)
  if (days < 0) return { label: `Overdue ${-days} day${days === -1 ? '' : 's'}`, tone: 'danger' }
  if (days === 0) return { label: 'Due today', tone: 'danger' }
  if (days === 1) return { label: 'Due tomorrow', tone: 'default' }
  return { label: `Due in ${days} days`, tone: 'default' }
}

// ── Filters ───────────────────────────────────────────────────────────

export type DueFilter = 'overdue' | 'next7' | 'next30'

export const DUE_FILTER_OPTIONS: { value: DueFilter; label: string }[] = [
  { value: 'overdue', label: 'Overdue' },
  { value: 'next7', label: 'Next 7 days' },
  { value: 'next30', label: 'Next 30 days' },
]

/** Due-date buckets only mean something for money still owed. */
export function matchesDueFilter(
  inv: { status: InvoiceStatus; dueDate?: string | null },
  filter: DueFilter,
  today: Date,
): boolean {
  if (!OPEN_LIABILITY_STATUSES.includes(inv.status) || !inv.dueDate) return false
  const days = daysUntil(inv.dueDate, today)
  if (filter === 'overdue') return days < 0
  if (filter === 'next7') return days >= 0 && days <= 7
  return days >= 0 && days <= 30
}

export interface SearchableInvoice {
  id: string
  invoiceNumber?: string | null
  supplierInvoiceNumber?: string | null
  referenceNumber?: string | null
  service?: string | null
  vendor?: { displayName?: string | null } | null
  client?: { displayName?: string | null } | null
  supplierSnapshot?: { name: string } | null
  customerSnapshot?: { name: string } | null
}

/** Case-insensitive match on invoice number, supplier's number, reference, service, supplier or customer name. */
export function matchesSearch(inv: SearchableInvoice, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  return [
    invoiceRef(inv).text,
    inv.supplierInvoiceNumber,
    inv.referenceNumber,
    inv.service,
    inv.vendor?.displayName,
    inv.client?.displayName,
    inv.supplierSnapshot?.name,
    inv.customerSnapshot?.name,
  ].some((v) => v?.toLowerCase().includes(q))
}

// ── Row cells ─────────────────────────────────────────────────────────

/**
 * Where the invoice sits in its approval journey, from its status alone. The finer step the mockup
 * names ("CFO authorization · Threshold > £1,000") is not on the schema — the page greys that part.
 */
export function approvalStep(status: InvoiceStatus): string {
  if (DRAFT_STATUSES.includes(status)) return 'Draft · not raised'
  if (PENDING_REVIEW_STATUSES.includes(status)) return 'Awaiting approval'
  if (status === 'HOLD') return 'On hold'
  if (status === 'ISSUED') return 'Sent · awaiting customer'
  if (status === 'MANAGER_RAISED' || status === 'MANAGER_APPROVED') return 'Manager step'
  if (status === 'MANAGER_REJECTED' || status === 'RECALLED') return 'Returned for changes'
  if (status === 'PAYMENT_FAILED') return 'Payment failed'
  if (status === 'REJECTED') return 'Returned to supplier'
  if (PAID_STATUSES.includes(status)) return 'Settled'
  if (APPROVED_SCHEDULED_STATUSES.includes(status)) return 'Approved · awaiting payment'
  if (status === 'CANCELLED' || status === 'VOID') return 'Closed'
  return 'In progress'
}

/** The row's one-click next step. `review` and `open` open the drawer; the rest are BE-pending. */
export type QuickAction = 'review' | 'open' | 'payment' | 'receipt'

export function quickAction(status: InvoiceStatus): QuickAction {
  if (PENDING_REVIEW_STATUSES.includes(status)) return 'review'
  if (PAID_STATUSES.includes(status)) return 'receipt'
  if (APPROVED_SCHEDULED_STATUSES.includes(status)) return 'payment'
  return 'open'
}

export interface TaxSplit {
  net: number
  tax: number
}

/**
 * Net + tax from the line items. `null` when no line carries a tax amount — a total with no tax
 * breakdown on file is shown as a total only, not as "£0 VAT", which would be a claim.
 */
export function taxSplit(inv: {
  amount?: number | null
  billingAmount?: number | null
  lineItems?: { taxAmount?: number | null }[] | null
}): TaxSplit | null {
  const lines = inv.lineItems ?? []
  if (!lines.some((l) => l.taxAmount != null)) return null
  const tax = lines.reduce((s, l) => s + (l.taxAmount ?? 0), 0)
  return { net: invoiceAmount(inv) - tax, tax }
}

// ── KPIs ──────────────────────────────────────────────────────────────

export interface KpiRow {
  status: InvoiceStatus
  currency: string
  amount?: number | null
  billingAmount?: number | null
}

/**
 * INV-PD — invoices settled in the same calendar month as `today` (by their `paidAt`). A paid invoice
 * with no recorded time is left out rather than guessed into a month.
 */
export function settledInMonth<T extends { status: InvoiceStatus; paidAt?: string | null }>(
  rows: readonly T[],
  today: Date,
): T[] {
  return rows.filter((r) => {
    if (!PAID_STATUSES.includes(r.status) || !r.paidAt) return false
    const d = new Date(r.paidAt)
    return d.getFullYear() === today.getFullYear() && d.getMonth() === today.getMonth()
  })
}

export function countOf(rows: KpiRow[], statuses: InvoiceStatus[]): number {
  const set = new Set(statuses)
  return rows.filter((r) => set.has(r.status)).length
}

// ── CSV ───────────────────────────────────────────────────────────────

export interface CsvInvoice extends SearchableInvoice {
  status: InvoiceStatus
  displayStatus?: string | null
  currency: string
  amount?: number | null
  billingAmount?: number | null
  invoiceDate: string
  dueDate?: string | null
  lineItems?: { taxAmount?: number | null }[] | null
}

const CSV_HEADER = [
  'Invoice',
  'Supplier invoice no.',
  'Reference',
  'Supplier',
  'Invoice date',
  'Due date',
  'Currency',
  'Net',
  'Tax',
  'Total',
  'Status',
]

function csvCell(v: string | number | null | undefined): string {
  const s = v == null ? '' : String(v)
  // Quote when needed, and neutralise spreadsheet formula injection (=, +, -, @ at the start).
  const safe = /^[=+\-@]/.test(s) ? `'${s}` : s
  return /[",\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe
}

/** The selected invoices as CSV (amounts as plain numbers in the invoice's own currency). */
export function invoicesToCsv(rows: CsvInvoice[]): string {
  const lines = rows.map((inv) => {
    const split = taxSplit(inv)
    return [
      invoiceRef(inv).text,
      inv.supplierInvoiceNumber,
      inv.referenceNumber,
      inv.vendor?.displayName,
      inv.invoiceDate?.slice(0, 10),
      inv.dueDate?.slice(0, 10),
      inv.currency,
      split ? split.net.toFixed(2) : '',
      split ? split.tax.toFixed(2) : '',
      invoiceAmount(inv).toFixed(2),
      inv.displayStatus ?? inv.status,
    ]
      .map(csvCell)
      .join(',')
  })
  return [CSV_HEADER.join(','), ...lines].join('\n')
}

// ── Filter options & joins ────────────────────────────────────────────

export interface FacetOption {
  value: string
  label: string
}

/** One option per supplier present in the rows (by party id), sorted by name. */
export function supplierOptions(
  rows: { supplierPartyId?: string | null; vendor?: { displayName?: string | null } | null }[],
): FacetOption[] {
  const byId = new Map<string, string>()
  for (const r of rows) {
    if (r.supplierPartyId && !byId.has(r.supplierPartyId)) {
      byId.set(r.supplierPartyId, r.vendor?.displayName || 'Unnamed supplier')
    }
  }
  return [...byId].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label))
}

/** One option per currency present in the rows, sorted. */
export function currencyOptions(rows: { currency: string }[]): FacetOption[] {
  return [...new Set(rows.map((r) => r.currency))].sort().map((c) => ({ value: c, label: c }))
}

export interface EntityRef {
  id: string
  name: string
  jurisdiction?: string | null
  entity_type?: string | null
}

/** "PRIVATE_LIMITED" → "Private limited". */
function humanise(code: string): string {
  const s = code.replace(/_/g, ' ').toLowerCase()
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/** The paying entity's name and a "Jurisdiction · Type" line, or null when it is not one of ours. */
export function entityLabel(
  id: string | null | undefined,
  byId: Map<string, EntityRef>,
): { name: string; detail: string } | null {
  const le = id ? byId.get(id) : undefined
  if (!le) return null
  const detail = [le.jurisdiction, le.entity_type ? humanise(le.entity_type) : null].filter(Boolean).join(' · ')
  return { name: le.name, detail }
}
